import React from 'react';
import {
  FissionV4Broadcast,
  FissionV4ProgressRecord,
} from '../../../fission-v4.service';
import {formatAppDate, toPriceStr} from '@/utils';
import {
  DEFAULT_FALLBACK_TARGET_INDEX,
  DEFAULT_REWARD_VALUES,
  POINTER_TARGET_ANGLE_DEG,
  REWARD_START_DEG,
  REWARD_STEP_DEG,
  withdrawalTickerMessages,
} from '../constants';
import {AwardRecordItem, GuideButtonRect} from '../types';

export const polarToPercentStyle = (
  radiusPercent: number,
  angleDeg: number,
) => {
  const angle = (angleDeg * Math.PI) / 180;
  const x = Math.cos(angle) * radiusPercent;
  const y = Math.sin(angle) * radiusPercent;

  return {
    left: `${(50 + x).toFixed(2)}%`,
    top: `${(50 + y).toFixed(2)}%`,
  };
};

export const normalizeRotation = (rotation: number) => {
  const normalized = rotation % 360;
  return normalized < 0 ? normalized + 360 : normalized;
};

export const getTargetRotationByIndex = (index: number) => {
  const rewardCenterAngle = REWARD_START_DEG + index * REWARD_STEP_DEG;
  return normalizeRotation(POINTER_TARGET_ANGLE_DEG - rewardCenterAngle);
};

export const getClockwiseDelta = (fromDeg: number, toDeg: number) =>
  normalizeRotation(toDeg - fromDeg);

export const getCounterClockwiseDelta = (fromDeg: number, toDeg: number) =>
  -normalizeRotation(fromDeg - toDeg);

export const getCounterClockwiseCenteredTargetRotation = (
  startRotation: number,
  targetIndex: number,
  minExtraRotations: number,
) => {
  const currentNormalized = normalizeRotation(startRotation);
  const targetNormalized = getTargetRotationByIndex(targetIndex);
  const targetDelta = getCounterClockwiseDelta(
    currentNormalized,
    targetNormalized,
  );
  const targetDistance = Math.abs(targetDelta);
  const minExtraDegrees = minExtraRotations * 360;
  const extraFullRotations =
    targetDistance >= minExtraDegrees
      ? 0
      : Math.ceil((minExtraDegrees - targetDistance) / 360);

  return startRotation - extraFullRotations * 360 + targetDelta;
};

export const getClockwiseCenteredTargetRotation = (
  startRotation: number,
  targetIndex: number,
  minExtraRotations: number,
) => {
  const currentNormalized = normalizeRotation(startRotation);
  const targetNormalized = getTargetRotationByIndex(targetIndex);
  const targetDelta = getClockwiseDelta(currentNormalized, targetNormalized);
  const minExtraDegrees = minExtraRotations * 360;
  const extraFullRotations =
    targetDelta >= minExtraDegrees
      ? 0
      : Math.ceil((minExtraDegrees - targetDelta) / 360);

  return startRotation + extraFullRotations * 360 + targetDelta;
};

export const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3);

export const easeOutQuart = (progress: number) => 1 - Math.pow(1 - progress, 4);

export const easeOutQuint = (progress: number) => 1 - Math.pow(1 - progress, 5);

export const easeOutSext = (progress: number) => 1 - Math.pow(1 - progress, 6);

export const easeOutQuad = (progress: number) => 1 - Math.pow(1 - progress, 2);

export const formatCountdown = (totalSeconds: number) => {
  const safeSeconds = Math.max(totalSeconds, 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  return [hours, minutes, seconds]
    .map(value => String(value).padStart(2, '0'))
    .join(':');
};

export const getCountdownEndAt = (seconds: number) =>
  Date.now() + Math.max(Number(seconds || 0), 0) * 1000;

export const getRemainingSeconds = (endAt: number) =>
  Math.max(Math.ceil((endAt - Date.now()) / 1000), 0);

export const formatDayCountdown = (totalSeconds: number) => {
  const safeSeconds = Math.max(totalSeconds, 0);
  const days = Math.floor(safeSeconds / 86400);
  const hours = Math.floor((safeSeconds % 86400) / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  return `${days}d:${String(hours).padStart(2, '0')}h:${String(
    minutes,
  ).padStart(2, '0')}m:${String(seconds).padStart(2, '0')}s`;
};

export const formatExpiresCountdown = (totalSeconds: number) => {
  const safeSeconds = Math.max(totalSeconds, 0);
  const days = Math.floor(safeSeconds / 86400);
  const hours = Math.floor((safeSeconds % 86400) / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  return `${String(days).padStart(2, '0')} d ${String(hours).padStart(
    2,
    '0',
  )}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const formatAmount = (amount?: number) =>
  toPriceStr(Number(amount || 0), {thousands: true, fixed: 2});

export const formatProgressAmount = (amount?: number) => {
  const numericAmount = Number(amount || 0);

  if (!Number.isFinite(numericAmount)) {
    return '0.00';
  }

  return (Math.round(numericAmount * 100) / 100).toFixed(2);
};

export const formatRewardLabel = (amount?: number) => {
  const numericAmount = Number(amount || 0);
  return String(numericAmount);
};

export const getRewardLabelValue = (reward: string) => {
  const value = reward.includes('-') ? reward.split('-').pop() : reward;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? 0 : numericValue;
};

export const formatAwardItems = (awardItems: number[]) => {
  const displayItems = awardItems.slice(0, DEFAULT_REWARD_VALUES.length);
  const minValue = Math.min(...displayItems.map(item => Number(item || 0)));

  return displayItems.map(item => {
    const numericValue = Number(item || 0);
    const label = formatRewardLabel(numericValue);
    return numericValue === minValue ? `0-${label}` : label;
  });
};

export const getRewardAmountByIndex = (rewards: string[], index: number) => {
  return getRewardLabelValue(rewards[index] || '');
};

export const getFixedSpinTargetIndex = (rewards: string[]) => {
  const rangeRewardIndex = rewards.findIndex(reward =>
    reward.trim().startsWith('0-'),
  );

  return rangeRewardIndex >= 0
    ? rangeRewardIndex
    : DEFAULT_FALLBACK_TARGET_INDEX;
};

export const maskUserId = (userId?: number) => {
  const text = String(userId || '');
  const suffix = text ? text.slice(-3).padStart(3, '0') : '001';

  return `Player***${suffix}`;
};

export const fillTickerMessages = (
  messages: string[],
  fallbackMessages = withdrawalTickerMessages,
) => {
  const nextMessages = messages.filter(Boolean);

  if (nextMessages.length >= fallbackMessages.length) {
    return nextMessages;
  }

  return [
    ...nextMessages,
    ...fallbackMessages.slice(0, fallbackMessages.length - nextMessages.length),
  ];
};

const formatRecordTime = (time?: string | number) => {
  if (time == null || time === '') {
    return '-';
  }

  const normalizedTime =
    typeof time === 'string' && /^\d+$/.test(time) ? Number(time) : time;
  const formattedTime = formatAppDate(normalizedTime, 'YYYY-MM-DD HH:mm:ss');

  return formattedTime === 'Invalid Date' ? '-' : formattedTime;
};

export const mapProgressRecordToAwardRecord = (
  item: FissionV4ProgressRecord,
): AwardRecordItem => ({
  account: 'User',
  amount: formatAmount(item.rewardAmount ?? item.frontDisplayValue),
  time: formatRecordTime(item.createTime),
});

export const mapBroadcastToTickerMessage = (item: FissionV4Broadcast) => {
  const user = item.nickName || maskUserId(item.userId);
  const amount = formatAmount(item.rewardAmount);
  return `${user} success withdrawal ${amount}`;
};

const fallbackBroadcastUserIds = [1, 18, 127, 236, 452];

export const buildBroadcastTickerMessages = (
  broadcasts: FissionV4Broadcast[],
  targetAmount?: number,
) => {
  const normalizedTargetAmount = Number(targetAmount);
  const hasValidTargetAmount =
    Number.isFinite(normalizedTargetAmount) && normalizedTargetAmount > 0;
  if (!hasValidTargetAmount) {
    return [];
  }

  const messages = broadcasts.map(item =>
    mapBroadcastToTickerMessage({
      ...item,
      rewardAmount: normalizedTargetAmount,
    }),
  );
  const fallbackMessages = fallbackBroadcastUserIds.map(userId =>
    mapBroadcastToTickerMessage({
      userId,
      rewardAmount: normalizedTargetAmount,
    }),
  );

  return fillTickerMessages(messages, fallbackMessages);
};

export const getGuideButtonStyle = (
  rect: GuideButtonRect | null,
): React.CSSProperties => {
  if (rect) {
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  }

  return {
    left: '50%',
    top: '50%',
    width: '6.625rem',
    height: '7.375rem',
    transform: 'translate(-50%, -50%)',
  };
};

export const getRewardModalOverlayClassName = (
  closing: boolean,
  backgroundClassName = 'bg-black/80',
) =>
  [
    `fixed inset-0 left-1/2 z-[20000] flex h-screen w-full max-w-[500px] -translate-x-1/2 items-center justify-center overflow-hidden ${backgroundClassName}`,
    closing
      ? 'animate-[refer-earn-modal-backdrop-out_220ms_ease-in_forwards]'
      : 'animate-[refer-earn-modal-backdrop-in_220ms_ease-out_forwards]',
  ].join(' ');

export const getRewardModalPanelClassName = (closing: boolean) =>
  [
    'relative h-screen max-h-[41.6875rem] w-full max-w-[23.4375rem] overflow-hidden',
    closing
      ? 'animate-[refer-earn-modal-panel-out_220ms_ease-in_forwards]'
      : 'animate-[refer-earn-modal-panel-in_220ms_ease-out_forwards]',
  ].join(' ');
